<template>
  <div class="view-container">
    <!-- 连接切换栏 -->
    <div class="conn-bar">
      <div class="conn-bar-left">
        <i class="fas fa-database"></i>
        <span class="conn-label">连接：</span>
        <select v-model="currentConnId" class="conn-select" @change="onSwitchConnection">
          <option v-for="conn in connections" :key="conn.id" :value="conn.id">{{ conn.name }}</option>
        </select>
        <span v-if="currentConn" class="conn-detail">{{ currentConn.server }} / {{ currentConn.database }}</span>
        <button class="conn-refresh-btn" @click="loadConnections" title="刷新连接"><i class="fas fa-sync-alt"></i></button>
      </div>
      <div class="conn-bar-right">
        <span v-if="currentConn && currentConn.isDefault" class="conn-tag default-tag">默认</span>
        <span v-if="currentConn && !currentConn.isDefault" class="conn-tag ds-tag">数据源</span>
      </div>
    </div>

    <!-- SQL编辑器 -->
    <div class="editor-area">
      <div class="editor-toolbar">
        <div class="toolbar-left">
          <span v-if="currentConn" class="db-label">{{ currentConn.database }}</span>
          <span v-else class="db-label">-</span>
          <span class="sep">|</span>
          <button class="tool-btn execute" @click="executeSql" :disabled="executing || !currentConnId" title="执行 (Ctrl+Enter)">
            <i class="fas fa-play"></i> 执行
          </button>
          <button class="tool-btn" @click="stopExecution" :disabled="!executing" title="停止">
            <i class="fas fa-stop"></i> 停止
          </button>
          <span class="sep">|</span>
          <button class="tool-btn" @click="clearEditor" title="清空">
            <i class="fas fa-eraser"></i> 清空
          </button>
        </div>
        <div class="toolbar-right">
          <span v-if="lastExecTime" class="exec-time">
            <i class="fas fa-clock"></i> {{ lastExecTime }}
          </span>
        </div>
      </div>
      <div class="editor-body">
        <div class="line-numbers" ref="lnRef">
          <div v-for="n in lineCount" :key="n" class="line-num">{{ n }}</div>
        </div>
        <textarea ref="editorRef" v-model="sqlText" class="sql-editor" spellcheck="false"
          placeholder="-- 在此输入SQL语句，Ctrl+Enter 执行&#10;-- 多条语句请用分号隔开&#10;-- SELECT * FROM Users WHERE IsActive = 1"
          @keydown="handleKeydown" @scroll="syncScroll" @input="updateLineCount"></textarea>
      </div>
    </div>

    <!-- 结果区 -->
    <div class="result-area">
      <div class="result-tabs">
        <button :class="{ active: resultTab === 'results' }" @click="resultTab = 'results'">
          结果 <span v-if="resultSets.length">({{ resultSets.length }})</span>
        </button>
        <button :class="{ active: resultTab === 'messages' }" @click="resultTab = 'messages'">
          消息 <span v-if="messages.length">({{ messages.length }})</span>
        </button>
      </div>
      <div class="result-body">
        <div v-if="resultTab === 'results'" class="results-view">
          <div v-if="resultSets.length === 0 && affectedRows === null && !executing" class="result-empty">
            <i class="fas fa-terminal"></i>
            <p>点击「执行」或按 Ctrl+Enter 运行SQL</p>
          </div>
          <div v-if="executing" class="result-loading">
            <i class="fas fa-spinner fa-spin"></i> 正在执行...
          </div>
          <div v-for="(rs, rsIdx) in resultSets" :key="rsIdx" class="result-set">
            <div class="rs-header">
              <i class="fas fa-table"></i>
              <span>结果集 {{ rsIdx + 1 }}</span>
              <span class="rs-meta">({{ rs.rows.length }} 行, {{ rs.columns.length }} 列)</span>
              <span class="rs-time">耗时: {{ rs.elapsed }}ms</span>
            </div>
            <div class="rs-table-wrapper">
              <table class="rs-table">
                <thead>
                  <tr>
                    <th class="row-num-th">#</th>
                    <th v-for="col in rs.columns" :key="col">{{ col }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in rs.rows.slice(0, 500)" :key="ri">
                    <td class="row-num">{{ ri + 1 }}</td>
                    <td v-for="col in rs.columns" :key="col" :class="{ 'null-cell': row[col] === null }">
                      {{ row[col] !== null ? String(row[col]) : 'NULL' }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="rs.rows.length > 500" class="rs-truncated">仅显示前 500 行，共 {{ rs.rows.length }} 行</div>
            </div>
          </div>
          <div v-if="affectedRows !== null && resultSets.length === 0" class="affected-info">
            <i class="fas fa-check-circle"></i>
            <span>受影响的行数: {{ affectedRows }}</span>
            <span class="rs-time">总耗时: {{ totalElapsed }}ms</span>
          </div>
        </div>
        <div v-if="resultTab === 'messages'" class="messages-view">
          <div class="msg-item" v-for="(msg, idx) in messages" :key="idx" :class="msg.type">
            <span class="msg-time">{{ msg.time }}</span>
            <span class="msg-text">{{ msg.text }}</span>
          </div>
          <div v-if="messages.length === 0" class="result-empty"><p>暂无消息</p></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { getConnections, executeSql as apiExecute } from '../api/database'

const connections = ref([])
const currentConnId = ref('')
const sqlText = ref(`SELECT TOP 20 * FROM SYSUser WHERE Active=1
`)
const editorRef = ref(null)
const lnRef = ref(null)
const lineCount = ref(1)
const executing = ref(false)
const resultTab = ref('results')
const resultSets = ref([])
const messages = ref([])
const affectedRows = ref(null)
const totalElapsed = ref(0)
const lastExecTime = ref('')

const currentConn = computed(() => connections.value.find(c => c.id === currentConnId.value))

const loadConnections = async () => {
  try {
    const res = await getConnections()
    const list = res.Data || res.data || []
    connections.value = list
    if (list.length > 0 && !currentConnId.value) {
      currentConnId.value = list[0].id
    }
  } catch (e) {
    console.error('加载连接失败', e)
  }
}

onMounted(loadConnections)

const onSwitchConnection = () => {
  resultSets.value = []
  messages.value = []
  affectedRows.value = null
  addMessage('info', `已切换到: ${currentConn.value?.name}`)
}

function updateLineCount() { lineCount.value = (sqlText.value.match(/\n/g) || []).length + 1 }

function handleKeydown(e) {
  if (e.ctrlKey && e.key === 'Enter') { e.preventDefault(); executeSql() }
}

function syncScroll() {
  if (lnRef.value && editorRef.value) lnRef.value.scrollTop = editorRef.value.scrollTop
}

function clearEditor() {
  sqlText.value = '-- 在此输入SQL语句\n'
  updateLineCount()
}

function addMessage(type, text) {
  const now = new Date()
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
  messages.value.push({ type, text, time })
}

async function executeSql() {
  const sql = sqlText.value.trim()
  if (!sql) return
  if (!currentConnId.value) { addMessage('warning', '请先选择数据库连接'); return }

  executing.value = true
  resultSets.value = []
  affectedRows.value = null
  totalElapsed.value = 0
  resultTab.value = 'results'

  addMessage('info', `开始执行 SQL (${currentConn.value?.server}/${currentConn.value?.database})...`)

  try {
    const res = await apiExecute({ connectionId: currentConnId.value, sql })
    const data = res.Data || res.data || res

    if (res.ResultType === false || data.hasError) {
      resultTab.value = 'messages'
    }

    if (data.resultSets) {
      // normalizeKeys 把行数据的键转为 camelCase（如 UserId→userId），但 columns 是简单字符串数组不会转换
      // 导致 row["UserId"] 查找不到值。此处把行数据键还原为与 columns 一致的 PascalCase
      resultSets.value = data.resultSets.map(rs => {
        const camelToPascal = {}
        for (const col of rs.columns) {
          camelToPascal[col.charAt(0).toLowerCase() + col.slice(1)] = col
        }
        return {
          ...rs,
          rows: rs.rows.map(row => {
            const fixed = {}
            for (const [key, val] of Object.entries(row)) {
              fixed[camelToPascal[key] || key] = val
            }
            return fixed
          })
        }
      })
    }
    if (data.affectedRows != null) affectedRows.value = data.affectedRows
    if (data.totalElapsed) totalElapsed.value = data.totalElapsed
    if (data.messages) {
      data.messages.forEach(m => addMessage('info', m))
    }
    lastExecTime.value = `${data.totalElapsed || '?'}ms`
  } catch (e) {
    console.error('执行失败', e)
    const msg = e?.response?.data?.Msg || e?.response?.data?.msg || e?.message || '未知错误'
    addMessage('error', '执行失败: ' + msg)
    resultTab.value = 'messages'
  }

  executing.value = false
}

function stopExecution() {
  executing.value = false
  addMessage('warning', '用户取消了执行')
}

updateLineCount()
</script>

<style scoped>
.view-container { padding: 12px; background: #f5f7fa; min-height: calc(94vh - 110px); max-height: calc(94vh - 110px); display: flex; flex-direction: column; overflow: hidden; }

.conn-bar { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; background: #1f2937; border-radius: 6px; margin-bottom: 8px; flex-shrink: 0; }
.conn-bar-left { display: flex; align-items: center; gap: 8px; color: #d1d5db; font-size: 12px; }
.conn-bar-left i { color: #10b981; }
.conn-label { color: #9ca3af; }
.conn-select { padding: 4px 8px; border: 1px solid #374151; border-radius: 4px; background: #374151; color: #e5e7eb; font-size: 12px; outline: none; cursor: pointer; min-width: 160px; }
.conn-detail { color: #6b7280; font-size: 11px; }
.conn-refresh-btn { width: 24px; height: 24px; border: 1px solid #374151; border-radius: 4px; background: transparent; color: #9ca3af; cursor: pointer; font-size: 11px; display: flex; align-items: center; justify-content: center; }
.conn-refresh-btn:hover { background: #374151; color: #e5e7eb; }
.conn-tag { font-size: 10px; padding: 2px 8px; border-radius: 3px; }
.default-tag { background: #1b4332; color: #10b981; }
.ds-tag { background: #1e3a5f; color: #60a5fa; }

.editor-area { background: #fff; border: 1px solid #e8ecf1; border-radius: 6px; overflow: hidden; flex-shrink: 0; margin-bottom: 8px; }
.editor-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 5px 10px; background: #f8fafc; border-bottom: 1px solid #e5e7eb; }
.toolbar-left, .toolbar-right { display: flex; align-items: center; gap: 4px; }
.db-label { font-size: 12px; font-weight: 600; color: #10b981; font-family: monospace; }
.sep { color: #e5e7eb; margin: 0 4px; user-select: none; }
.tool-btn { padding: 3px 10px; border: 1px solid #e5e7eb; border-radius: 3px; background: #fff; color: #374151; font-size: 11px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.15s; }
.tool-btn:hover { background: #f3f4f6; }
.tool-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.tool-btn.execute { background: #10b981; color: #fff; border-color: #059669; }
.tool-btn.execute:hover:not(:disabled) { background: #059669; }
.exec-time { font-size: 11px; color: #9ca3af; display: flex; align-items: center; gap: 4px; }

.editor-body { display: flex; background: #1e293b; }
.line-numbers { padding: 10px 0; background: #1a2332; color: #4a5568; font-size: 13px; font-family: 'Consolas', 'Courier New', monospace; text-align: right; user-select: none; overflow: hidden; min-width: 40px; }
.line-num { padding: 0 8px; line-height: 1.65; }
.sql-editor { flex: 1; padding: 10px 12px; background: #1e293b; color: #e2e8f0; border: none; font-size: 13px; font-family: 'Consolas', 'Courier New', monospace; line-height: 1.65; resize: none; outline: none; min-height: 150px; tab-size: 2; }
.sql-editor::placeholder { color: #4a5568; }

.result-area { flex: 1; background: #fff; border: 1px solid #e8ecf1; border-radius: 6px; display: flex; flex-direction: column; overflow: hidden; min-height: 0; }
.result-tabs { display: flex; border-bottom: 1px solid #e8ecf1; flex-shrink: 0; }
.result-tabs button { padding: 7px 16px; border: none; background: none; font-size: 12px; color: #6b7280; cursor: pointer; border-bottom: 2px solid transparent; display: flex; align-items: center; gap: 6px; }
.result-tabs button:hover { color: #374151; }
.result-tabs button.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; }
.result-tabs button span { font-size: 10px; background: #f3f4f6; padding: 1px 6px; border-radius: 10px; }
.result-tabs button.active span { background: #d1fae5; color: #065f46; }
.result-body { flex: 1; overflow: auto; }
.result-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #9ca3af; padding: 40px; }
.result-empty i { font-size: 36px; margin-bottom: 12px; color: #d1d5db; }
.result-empty p { font-size: 13px; }
.result-loading { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 20px; color: #10b981; font-size: 13px; }
.results-view { padding: 0; }
.result-set { border-bottom: 1px solid #e8ecf1; }
.rs-header { display: flex; align-items: center; gap: 8px; padding: 8px 14px; background: #f8fafc; font-size: 12px; color: #374151; border-bottom: 1px solid #e8ecf1; }
.rs-header i { color: #10b981; }
.rs-meta { color: #6b7280; font-size: 11px; }
.rs-time { margin-left: auto; color: #9ca3af; font-size: 11px; }
.rs-table-wrapper { overflow: auto; max-height: 500px; }
.rs-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: auto; }
.rs-table th, .rs-table td { padding: 6px 14px; text-align: left; white-space: nowrap; max-width: 320px; overflow: hidden; text-overflow: ellipsis; }
.rs-table th { background: #e5e7eb; font-weight: 700; color: #1f2937; position: sticky; top: 0; z-index: 2; border-bottom: 2px solid #d1d5db; }
.rs-table td { border-bottom: 1px solid #f0f0f0; border-right: 1px solid #f5f5f5; color: #1f2937; }
.rs-table td:last-child { border-right: none; }
.rs-table tbody tr:nth-child(even) { background: #f9fafb; }
.rs-table tbody tr:hover { background: #ecfdf5; }
.row-num-th { width: 44px; color: #6b7280; text-align: center; font-size: 11px; background: #f3f4f6 !important; }
.row-num { color: #9ca3af; text-align: center; font-size: 11px; background: #fafafa; user-select: none; }
.rs-table tbody tr:nth-child(even) .row-num { background: #f5f5f5; }
.null-cell { color: #f87171; font-style: italic; font-size: 11px; text-align: center; }
.rs-truncated { padding: 8px 14px; background: #fef9c3; color: #a16207; font-size: 12px; text-align: center; border-top: 1px solid #fde68a; }

.affected-info { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #dcfce7; color: #16a34a; font-size: 13px; }
.affected-info i { color: #16a34a; }
.affected-info .rs-time { color: #6b7280; font-size: 11px; }

.messages-view { padding: 8px; }
.msg-item { display: flex; gap: 12px; padding: 5px 10px; font-size: 12px; font-family: monospace; border-bottom: 1px solid #f5f5f5; }
.msg-time { color: #9ca3af; white-space: nowrap; }
.msg-text { color: #374151; }
.msg-item.error .msg-text { color: #dc2626; }
.msg-item.warning .msg-text { color: #d97706; }

.fa-spinner { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
